import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from '../../../../util/reactIntl';
import { types as sdkTypes } from '../../../../util/sdkLoader';

import SearchSection from '../SearchSection/AlgoliaSearchSection';
import DesktopSearchBar from '../SearchSection/DesktopSearchBar';
import CustomHits from './Hits/Hits';

import Map from './Map/Map';

import css from './MainSearchContent.module.css';
import { parse } from '../../../../util/urlHelpers';
import IconCollection from '../../../../components/IconCollection/IconCollection';
import { extractLocationComponents } from '../../../../util/data';
import classNames from 'classnames';
import Container from '../../../../components/Layout/Container';
import Grid from '../../../../components/Layout/Grid';
import Column from '../../../../components/Layout/Column';
import { refineSearchByMapBounds } from '../AlgoliaSearchPage.duck';
import { buildAlgoliaFilters } from '../AlgoliaSearchPage.duck';
import { prefetchListing } from '../../../../containers/ListingPage/ListingPage.duck';
import { getAlgoliaIndexNameForLang } from '../../../../util/urlHelpers';
import { SUPPORTED_LANGUAGES } from '../../../../util/constants';

const { LatLngBounds, LatLng, UUID } = sdkTypes;

const MODAL_BREAKPOINT = 1024;
const PANEL_MIN_MARGIN = -230;
const PANEL_MAX_MARGIN = 0;
const PANEL_RANGE = PANEL_MAX_MARGIN - PANEL_MIN_MARGIN || 1;
const PANEL_AUTO_OPEN_THRESHOLD = -91;
const PANEL_DRAG_ACTIVATION_THRESHOLD = 8;
const PANEL_HORIZONTAL_DOMINANCE_RATIO = 1.2;
const PANEL_OFFSET_DRAG_HANDLE_OFFSET = 70;
const PANEL_MIN_MARGIN_THRESHOLD = 5;

export function getCenterFromBounds(bounds) {
  if (bounds && bounds.ne && bounds.sw) {
    const north = bounds.ne.lat;
    const south = bounds.sw.lat;
    const east = bounds.ne.lng;
    const west = bounds.sw.lng;

    const centerLatitude = (north + south) / 2;
    const centerLongitude = (east + west) / 2;

    return { lat: centerLatitude, lng: centerLongitude };
  }
  return null;
}

export const MAX_HITS = 150;

export const PRODUCT_CLICKED_EVENT = 'Product Clicked';
export const STORE_CLICKED_EVENT = 'Store Clicked';
export const coverEarthRadius = 20037500; // Radius to cover the entire earth's circumference from a given point in meters.

export const LOCAL_STORAGE_MAP_KEY = 'mapPreference';

export default function MainSearchContent(props) {
  const {
    facets,
    facetsInProgress,
    facetsError,
    algoliaQueryInProgress,
    algoliaBackgroundQueryInProgress,
    algoliaQueryError,
    algoliaListings,
    algoliaStores,
    currentUserLocationInfo,
    currentUserCurrency,
    currentUser,
    exchangeRates,
    config,
    history,
    routesConfiguration,
    onActivateListing,
    location,
    intl,
    scrolling,
    locale,
    defaultSort,
    dateRange,
    onDateRangeChange,
  } = props;

  const search = props.location?.search || {};

  const queryParams = useMemo(() => parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  }), [search]);

  const defaultMapPreference = useMemo(() =>
    typeof window !== 'undefined' && window.localStorage
      ? window.localStorage.getItem(LOCAL_STORAGE_MAP_KEY)
      : null,
    []
  );

  const preference = useMemo(() =>
    defaultMapPreference
      ? JSON.parse(defaultMapPreference).open
      : false,
    [defaultMapPreference]
  );

  const [showMap, setShowMap] = useState(preference);
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  const [panelOffset, setPanelOffset] = useState(PANEL_MIN_MARGIN);
  const [isMapPreviewVisible, setIsMapPreviewVisible] = useState(true);
  const [autoCloseEnabled, setAutoCloseEnabled] = useState(false);
  const [isDraggingPanel, setIsDraggingPanel] = useState(false);
  const [isMapExploring, setIsMapExploring] = useState(false);
  const [lockedMapViewportHeight, setLockedMapViewportHeight] = useState(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth <= MODAL_BREAKPOINT
  );

  const dispatch = useDispatch();
  const onPrefetchListing = useCallback(
    listingId => dispatch(prefetchListing(listingId, config)),
    [dispatch, config]
  );
  const dragStartYRef = useRef(0);
  const dragStartXRef = useRef(0);
  const dragStartOffsetRef = useRef(PANEL_MIN_MARGIN);
  const isDraggingPanelRef = useRef(false);
  const pendingDragRef = useRef(false);
  const dragPointerTargetRef = useRef(null);
  const dragPointerIdRef = useRef(null);
  const dragDirectionRef = useRef(null);
  const panelAnimationFrameRef = useRef(null);
  const panelOffsetRef = useRef(PANEL_MIN_MARGIN);
  const leftContentRef = useRef(null);
  const searchInputsWrapperRef = useRef(null);
  const mapMoveDebounceRef = useRef(null);
  const lastMapBoundsRef = useRef(null);
  const mainSearchContentRef = useRef(null);

  const defaultOrigin = props.currentUserLocationInfo?.origin;
  const { lng, lat } = defaultOrigin || {};
  const { bounds: qBounds, address: qAddress, city: qSelectedArea, keyword: qKeyword, origin: qOrigin, category, access_to_rentals, Condition_of_the_item, Trust_and_verification, cancelation_policy, reason_for_listing, security_deposit, priceRange, sort: qSort, country: qCountry } = queryParams || {};
  // Dates live in React state (not URL) to prevent loadData re-fires and scroll-to-top on date changes
  const qStartDate = dateRange?.startDate || null;
  const qEndDate = dateRange?.endDate || null;
  const userToken = currentUser?.id?.uuid;

  const activeFilters = useMemo(() => {
    const rawPairs = [
      ['category', category],
      ['access_to_rentals', access_to_rentals],
      ['Condition_of_the_item', Condition_of_the_item],
      ['Trust_and_verification', Trust_and_verification],
      ['cancelation_policy', cancelation_policy],
      ['reason_for_listing', reason_for_listing],
      ['security_deposit', security_deposit],
    ];

    const formatFacetValue = (value) => {
      const str = String(value);
      const needsQuotes = /\\s|:|\"/.test(str);
      const escaped = str.replace(/\"/g, '\\"');
      return needsQuotes ? `\"${escaped}\"` : escaped;
    };

    return rawPairs
      .filter(([, value]) => value !== null && value !== undefined && `${value}`.length > 0)
      .map(([key, value]) => `publicData.${key}:${formatFacetValue(value)}`);
  }, [
    category,
    access_to_rentals,
    Condition_of_the_item,
    Trust_and_verification,
    cancelation_policy,
    reason_for_listing,
    security_deposit,
  ]);

  // Reset map exploring state when location/search params change (user selects new location)
  useEffect(() => {
    setIsMapExploring(false);
    lastMapBoundsRef.current = null;
  }, [qAddress, qBounds, qKeyword, qSelectedArea]);

  const isInitialSearchMount = useRef(true);
  useEffect(() => {
    if (isInitialSearchMount.current) {
      isInitialSearchMount.current = false;
      return;
    }
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [qKeyword, qAddress, qBounds, qSelectedArea, category, qSort]);

  // Memoize all listings for map
  const allListings = useMemo(
    () => [...algoliaListings.relevant, ...algoliaListings.irrelevant],
    [algoliaListings.relevant, algoliaListings.irrelevant]
  );

  // Update mobile state on resize
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setIsMobile(window.innerWidth <= MODAL_BREAKPOINT);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Sync showMap state with parent on mount and changes
  useEffect(() => {
    if (props.onShowMapChange) {
      props.onShowMapChange(showMap);
    }
  }, [showMap, props.onShowMapChange]);

  // Highlight first listing when map opens on mobile
  useEffect(() => {
    if (showMap && isMobile) {
      // On mobile, when map opens, highlight the first listing
      if (allListings.length > 0 && onActivateListing) {
        const firstListing = allListings[0];
        if (firstListing?.objectID) {
          // Use setTimeout to ensure the map is ready
          const timeoutId = setTimeout(() => {
            onActivateListing(new UUID(firstListing.objectID));
          }, 100);
          return () => clearTimeout(timeoutId);
        }
      }
    }
  }, [showMap, isMobile]);

  // Memoize default bounds calculations
  const defaultBounds = useMemo(() => {
    if (!lng || !lat) return null;
    const kmToDeg = 50 / 111;
    const bounds = [
      new LatLng(lat - kmToDeg, lng - kmToDeg),
      new LatLng(lat + kmToDeg, lng + kmToDeg),
    ];
    return new LatLngBounds(bounds[1], bounds[0]);
  }, [lng, lat]);

  const selectedBounds = useMemo(
    () => qBounds || defaultBounds,
    [qBounds, defaultBounds]
  );

  // Location is available either by query param or current user's location
  const hasLocationAvailable = useMemo(
    () => !!qAddress || (lat && lng),
    [qAddress, lat, lng]
  );

  // Memoize selected area
  const selectedArea = useMemo(() => {
    if (qSelectedArea) return qSelectedArea;
    if (qAddress) return extractLocationComponents(qAddress);
    return currentUserLocationInfo?.city || null;
  }, [qSelectedArea, qAddress, currentUserLocationInfo?.city]);

  const toggleMapHandler = useCallback((bool) => {
    setShowMap(bool);
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(
        LOCAL_STORAGE_MAP_KEY,
        JSON.stringify({ open: bool })
      );
    }
    // Exit fullscreen when closing map
    if (!bool) {
      setIsMapFullscreen(false);
      setIsDraggingPanel(false);
      isDraggingPanelRef.current = false;
    } else {
      // Reset dragging state when opening map
      setIsDraggingPanel(false);
      isDraggingPanelRef.current = false;
    }
    // Notify parent component about showMap change
    if (props.onShowMapChange) {
      props.onShowMapChange(bool);
    }
  }, [props.onShowMapChange]);

  // Callback to reset map exploring state when user manually selects a location
  const handleLocationSelection = useCallback(() => {
    setIsMapExploring(false);
    lastMapBoundsRef.current = null;
  }, []);

  const handleToggleFullscreen = useCallback((isFullscreen) => {
    setIsMapFullscreen(isFullscreen);
  }, []);

  const isMobileOverlayActive = useMemo(
    () => isMobile && showMap && !isMapFullscreen,
    [isMobile, showMap, isMapFullscreen]
  );

  const panelOpenProgress = useMemo(
    () => isMobileOverlayActive
      ? Math.min(1, Math.max(0, (panelOffset - PANEL_MIN_MARGIN) / PANEL_RANGE))
      : 0,
    [isMobileOverlayActive, panelOffset]
  );


  // When map is open on mobile, fix the map container to the viewport so it ignores
  // all parent padding/margin (inner <main>, Container, Grid, Column all add spacing).
  // position:fixed takes it out of flow entirely and pins it flush below the topbar.
  const getMainSearchContentStyle = useMemo(() => {
    if (!(isMobile && showMap)) {
      return {
        height: 'auto',
        overflow: 'initial',
        minHeight: lockedMapViewportHeight ? `${lockedMapViewportHeight}px` : '700px',
      };
    }

    const baseFixed = {
      position: 'fixed',
      top: 'var(--topbarHeight, 64px)',
      left: 0,
      right: 0,
      bottom: 0,
      overflow: 'hidden',
      zIndex: 1,
    };

    if (!isMobileOverlayActive || !isDraggingPanel) {
      return baseFixed;
    }

    // Shrink container from below as panel is dragged up — same visual as before
    const panelMovement = panelOffset - PANEL_MIN_MARGIN;
    const bottomOffset = Math.max(0, panelMovement * 1.2);
    return {
      ...baseFixed,
      bottom: `${bottomOffset}px`,
      transition: 'none',
    };
  }, [lockedMapViewportHeight, isMobile, showMap, isMobileOverlayActive, isDraggingPanel, panelOffset]);

  const clearPanelAnimation = () => {
    if (panelAnimationFrameRef.current) {
      cancelAnimationFrame(panelAnimationFrameRef.current);
      panelAnimationFrameRef.current = null;
    }
  };

  const animatePanelOffsetTo = target => {
    clearPanelAnimation();
    const animate = () => {
      let shouldContinue = false;
      setPanelOffset(prev => {
        const diff = target - prev;
        if (Math.abs(diff) <= 1) {
          panelOffsetRef.current = target;
          return target;
        }
        shouldContinue = true;
        const nextValue = prev + diff * 0.2;
        panelOffsetRef.current = nextValue;
        return nextValue;
      });

      if (shouldContinue) {
        panelAnimationFrameRef.current = requestAnimationFrame(animate);
      } else {
        panelAnimationFrameRef.current = null;
      }
    };

    panelAnimationFrameRef.current = requestAnimationFrame(animate);
  };

  // Calculate distance between searchInputsWrapper and leftContent
  const calculateElementsDistance = useCallback(() => {
    if (!searchInputsWrapperRef.current || !leftContentRef.current) {
      return null;
    }
    const searchInputsRect = searchInputsWrapperRef.current.getBoundingClientRect();
    const leftContentRect = leftContentRef.current.getBoundingClientRect();

    // Calculate the distance between the bottom of searchInputsWrapper and top of leftContent
    const distance = leftContentRect.top - searchInputsRect.bottom;
    return distance;
  }, []);

  useEffect(() => {
    panelOffsetRef.current = panelOffset;
    if (!isMobileOverlayActive) {
      return;
    }

    // Close map when searchInputsWrapper and leftContent are within 0-10px distance
    if (isDraggingPanelRef.current) {
      const distance = calculateElementsDistance();
      if (distance !== null && distance >= 0 && distance <= 10) {
        setIsMapPreviewVisible(false);
        if (showMap) {
          toggleMapHandler(false);
        }
        return;
      }
    }

    if (panelOffset >= PANEL_MAX_MARGIN && autoCloseEnabled) {
      setIsMapPreviewVisible(false);
      if (showMap) {
        toggleMapHandler(false);
      }
      return;
    }

    if (!isDraggingPanelRef.current && panelOffset <= PANEL_MIN_MARGIN + PANEL_MIN_MARGIN_THRESHOLD) {
      setIsMapPreviewVisible(true);
      setAutoCloseEnabled(false);
    }
  }, [panelOffset, isMobileOverlayActive, showMap, autoCloseEnabled, isDraggingPanel, calculateElementsDistance, toggleMapHandler]);



  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    const body = document.body;
    const html = document.documentElement;

    const previousBodyOverflow = body.style.overflow;
    const previousBodyHeight = body.style.height;
    const previousHtmlOverflow = html.style.overflow;
    const previousHtmlHeight = html.style.height;

    if (isMobile && showMap) {
      body.style.overflow = 'hidden';
      body.style.height = '100%';
      html.style.overflow = 'hidden';
      html.style.height = '100%';

      return () => {
        body.style.overflow = previousBodyOverflow;
        body.style.height = previousBodyHeight;
        html.style.overflow = previousHtmlOverflow;
        html.style.height = previousHtmlHeight;
      };
    }

    body.style.overflow = previousBodyOverflow;
    body.style.height = previousBodyHeight;
    html.style.overflow = previousHtmlOverflow;
    html.style.height = previousHtmlHeight;

    return undefined;
  }, [isMobile, showMap]);

  useEffect(() => {
    if (!(isMobile && showMap)) {
      setLockedMapViewportHeight(null);
      return;
    }

    const measureHeight = () => {
      const heightFromElement = mainSearchContentRef.current?.getBoundingClientRect().height;
      const fallbackHeight = typeof window !== 'undefined' ? window.innerHeight - 64 : null;
      const resolvedHeight = heightFromElement || fallbackHeight;

      if (resolvedHeight && resolvedHeight > 0) {
        setLockedMapViewportHeight(resolvedHeight);
      }
    };

    measureHeight();
    window.addEventListener('resize', measureHeight);

    return () => {
      window.removeEventListener('resize', measureHeight);
    };
  }, [isMobile, showMap]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    const root = document.documentElement;
    if (lockedMapViewportHeight) {
      root.style.setProperty('--mobile-map-height', `${lockedMapViewportHeight}px`);
    } else {
      root.style.removeProperty('--mobile-map-height');
    }

    return () => {
      root.style.removeProperty('--mobile-map-height');
    };
  }, [lockedMapViewportHeight]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (isMobile && showMap) {
      window.scrollTo({ top: 0, behavior: 'instant' in window ? 'instant' : 'auto' });
    }
  }, [isMobile, showMap]);

  useEffect(() => {
    return () => clearPanelAnimation();
  }, []);

  useEffect(() => {
    if (isMobileOverlayActive) {
      setIsMapPreviewVisible(true);
      setAutoCloseEnabled(false);
      setPanelOffset(PANEL_MIN_MARGIN);
      setIsDraggingPanel(false);
      isDraggingPanelRef.current = false;
    } else {
      setIsMapPreviewVisible(false);
      setAutoCloseEnabled(false);
      clearPanelAnimation();
      setPanelOffset(0);
      setIsDraggingPanel(false);
      isDraggingPanelRef.current = false;
    }
  }, [isMobileOverlayActive]);

  const clampPanelOffset = useCallback(
    value => Math.min(PANEL_MAX_MARGIN, Math.max(PANEL_MIN_MARGIN, value)),
    []
  );

  const updatePanelOffset = useCallback(
    nextOffset => {
      const clamped = clampPanelOffset(nextOffset);
      panelOffsetRef.current = clamped;
      setPanelOffset(clamped);
      return clamped;
    },
    [clampPanelOffset]
  );

  const getPointerClientY = event =>
    typeof event?.clientY === 'number' ? event.clientY : 0;

  const getPointerClientX = event =>
    typeof event?.clientX === 'number' ? event.clientX : 0;

  const startPanelDrag = useCallback(
    (event, targetOverride) => {
      pendingDragRef.current = false;
      clearPanelAnimation();
      setAutoCloseEnabled(false);
      isDraggingPanelRef.current = true;
      setIsDraggingPanel(true);
      dragDirectionRef.current = 'vertical';
      const captureTarget =
        targetOverride || dragPointerTargetRef.current || event.currentTarget;
      dragPointerTargetRef.current = captureTarget;
      if (captureTarget?.setPointerCapture) {
        dragPointerIdRef.current = event.pointerId;
        captureTarget.setPointerCapture(event.pointerId);
      }
    },
    [clearPanelAnimation]
  );

  const handlePanelPointerDown = useCallback(
    (event, options = {}) => {
      if (!isMobileOverlayActive) {
        return;
      }

      const { forceDrag = false } = options;
      dragStartYRef.current = getPointerClientY(event);
      dragStartXRef.current = getPointerClientX(event);
      dragStartOffsetRef.current = panelOffset;
      dragPointerTargetRef.current = event.currentTarget;
      dragDirectionRef.current = null;
      dragPointerIdRef.current = null;

      if (forceDrag) {
        startPanelDrag(event, event.currentTarget);
        return;
      }

      pendingDragRef.current = true;
    },
    [isMobileOverlayActive, panelOffset, startPanelDrag]
  );

  const handlePanelPointerMove = useCallback(
    event => {
      if (!isMobileOverlayActive) {
        return;
      }

      const currentY = getPointerClientY(event);
      const currentX = getPointerClientX(event);
      const deltaY = dragStartYRef.current - currentY;
      const deltaX = currentX - dragStartXRef.current;

      if (pendingDragRef.current) {
        const absDeltaY = Math.abs(deltaY);
        const absDeltaX = Math.abs(deltaX);
        if (
          absDeltaY >= PANEL_DRAG_ACTIVATION_THRESHOLD &&
          absDeltaY > absDeltaX
        ) {
          startPanelDrag(event, dragPointerTargetRef.current);
        } else if (
          absDeltaX >= PANEL_DRAG_ACTIVATION_THRESHOLD &&
          absDeltaX > absDeltaY * PANEL_HORIZONTAL_DOMINANCE_RATIO
        ) {
          pendingDragRef.current = false;
          dragDirectionRef.current = 'horizontal';
          dragPointerTargetRef.current = null;
          dragPointerIdRef.current = null;
          return;
        } else {
          return;
        }
      }

      if (!isDraggingPanelRef.current) {
        return;
      }

      event.preventDefault();
      const nextOffset = updatePanelOffset(
        dragStartOffsetRef.current + deltaY
      );
      if (nextOffset > PANEL_AUTO_OPEN_THRESHOLD) {
        setAutoCloseEnabled(true);
      }
    },
    [isMobileOverlayActive, startPanelDrag, updatePanelOffset]
  );

  const stopPanelDrag = useCallback(
    event => {
      if (pendingDragRef.current) {
        const captureTarget = dragPointerTargetRef.current;
        pendingDragRef.current = false;
        dragPointerTargetRef.current = null;
        if (captureTarget?.releasePointerCapture) {
          captureTarget.releasePointerCapture(event.pointerId);
        }
        return;
      }

      if (!isDraggingPanelRef.current) {
        return;
      }

      isDraggingPanelRef.current = false;
      setIsDraggingPanel(false);
      const captureTarget = dragPointerTargetRef.current;
      if (
        captureTarget?.releasePointerCapture &&
        dragPointerIdRef.current != null
      ) {
        captureTarget.releasePointerCapture(dragPointerIdRef.current);
      }
      dragPointerTargetRef.current = null;
      dragPointerIdRef.current = null;
      dragDirectionRef.current = null;

      const currentOffset = panelOffsetRef.current;
      if (currentOffset > PANEL_AUTO_OPEN_THRESHOLD) {
        setIsMapPreviewVisible(false);
        animatePanelOffsetTo(PANEL_MAX_MARGIN);
      } else {
        setIsMapPreviewVisible(true);
        setAutoCloseEnabled(false);
        animatePanelOffsetTo(PANEL_MIN_MARGIN);
      }
    },
    []
  );

  // Handler for map pan/zoom - refines search by viewport bounds
  const handleMapMoveEnd = useCallback((viewportBounds) => {
    if (!viewportBounds || !viewportBounds.ne || !viewportBounds.sw) {
      return;
    }

    // In global browse mode (no location), map panning must not override global results
    if (!hasLocationAvailable) {
      return;
    }

    // Guard against NaN bounds from map initialisation with no center
    const { ne, sw } = viewportBounds;
    if (isNaN(ne.lat) || isNaN(ne.lng) || isNaN(sw.lat) || isNaN(sw.lng)) {
      return;
    }

    // Check if bounds have changed significantly (avoid redundant queries)
    const boundsKey = `${viewportBounds.ne.lat.toFixed(3)},${viewportBounds.ne.lng.toFixed(3)},${viewportBounds.sw.lat.toFixed(3)},${viewportBounds.sw.lng.toFixed(3)}`;
    if (lastMapBoundsRef.current === boundsKey) {
      return;
    }
    lastMapBoundsRef.current = boundsKey;

    // Set exploring state
    setIsMapExploring(true);

    // Build current search params (preserve all filters, query, sort, etc.)
    const filters = buildAlgoliaFilters({
      category,
      access_to_rentals,
      Condition_of_the_item,
      Trust_and_verification,
      cancelation_policy,
      reason_for_listing,
      security_deposit,
      priceRange,
    });

    const listingIndexName = getAlgoliaIndexNameForLang(locale || 'en', SUPPORTED_LANGUAGES);
    // When exploring map, use 'createdAt_desc' instead of 'nearest' sort
    // Nearest sort doesn't make sense when exploring by map bounds
    const effectiveSort = isMapExploring ? 'createdAt_desc' : (qSort || defaultSort || 'nearest');
    const searchParams = {
      query: qKeyword == null || qKeyword === '' ? '' : String(qKeyword),
      filters,
      sort: effectiveSort,
      indexes: {
        listingIndex: {
          indexName: listingIndexName,
        },
      },
      fetchedEntites: ['listings'],
      minimumHits: 50,
    };

    // Clear existing debounce
    if (mapMoveDebounceRef.current) {
      clearTimeout(mapMoveDebounceRef.current);
    }

    // Debounce map move to avoid excessive API calls
    mapMoveDebounceRef.current = setTimeout(() => {
      dispatch(refineSearchByMapBounds(viewportBounds, searchParams));
    }, 600); // 600ms debounce
  }, [dispatch, qKeyword, category, access_to_rentals, Condition_of_the_item, Trust_and_verification, cancelation_policy, reason_for_listing, security_deposit, priceRange, qSort, defaultSort, locale, isMapExploring, hasLocationAvailable]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (mapMoveDebounceRef.current) {
        clearTimeout(mapMoveDebounceRef.current);
      }
    };
  }, []);

  // Memoize listings arrays passed as props to prevent unnecessary re-renders
  const allListingsForProps = useMemo(
    () => allListings,
    [allListings]
  );

  const relevantListingsForMap = useMemo(
    () => [...algoliaListings.relevant],
    [algoliaListings.relevant]
  );
  const shouldForceFullOpacity = isMobileOverlayActive && relevantListingsForMap.length === 0;

  const panelPointerHandlers = useMemo(() => {
    if (!isMobileOverlayActive) {
      return {};
    }
    return {
      onPointerDown: handlePanelPointerDown,
      onPointerMove: handlePanelPointerMove,
      onPointerUp: stopPanelDrag,
      onPointerCancel: stopPanelDrag,
    };
  }, [isMobileOverlayActive, handlePanelPointerDown, handlePanelPointerMove, stopPanelDrag]);

  // Helper function to create drag handle handlers with stopPropagation
  const createDragHandleHandlers = useCallback((forceDrag = false) => ({
    onPointerDown: (event) => {
      event.stopPropagation();
      handlePanelPointerDown(event, { forceDrag });
    },
    onPointerMove: (event) => {
      event.stopPropagation();
      handlePanelPointerMove(event);
    },
    onPointerUp: (event) => {
      event.stopPropagation();
      stopPanelDrag(event);
    },
    onPointerCancel: (event) => {
      event.stopPropagation();
      stopPanelDrag(event);
    },
  }), [handlePanelPointerDown, handlePanelPointerMove, stopPanelDrag]);

  return (
    <main>
      <Container className={css.mainContainer}>
        <Grid>
          <Column>
            <div className={css.mapButtonContainer}>
              {/* Show map */}
              {!showMap ? (
                <button
                  className={classNames(css.mapButton, scrolling && css.hidden)}
                  type="button"
                  onClick={(event) => {
                    event.preventDefault();
                    // Use requestAnimationFrame for better performance
                    if (typeof window !== 'undefined') {
                      requestAnimationFrame(() => {
                        window.scrollTo({ top: 150, behavior: 'smooth' });
                      });
                    } else {
                      setTimeout(() => {
                        window.scrollTo({ top: 150, behavior: 'smooth' });
                      }, 100);
                    }
                    toggleMapHandler(!showMap);
                  }}
                >
                  {/* <img src={mapIcon} alt="map" /> */}
                  <IconCollection name="map_pin" />
                  <span><FormattedMessage id="MainSearchContent.mapView" /></span>
                </button>
              ) : null}
            </div>
            <SearchSection
              currentUserLocationInfo={currentUserLocationInfo}
              currentUserCurrency={currentUserCurrency}
              currentUser={currentUser}
              exchangeRates={exchangeRates}
              intl={intl}
              setShowMap={setShowMap}
              showMap={showMap}
              config={config}
              history={history}
              routesConfiguration={routesConfiguration}
              queryParams={queryParams}
              selectedBounds={selectedBounds}
              onActivateListing={onActivateListing}
              algoliaListings={allListingsForProps}
              algoliaStores={algoliaStores}
              facetsInProgress={facetsInProgress}
              algoliaQueryInProgress={algoliaQueryInProgress}
              facets={facets}
              facetsError={facetsError}
              neighborhood={selectedArea}
              hasLocationAvailable={hasLocationAvailable}
              defaultSort={defaultSort}
              locale={locale}
              showOnlySearchInputs={isMobile && showMap}
              panelOffset={isMobile && showMap ? panelOffset : null}
              isDraggingPanel={isMobile && showMap ? isDraggingPanel : false}
              searchInputsWrapperRef={searchInputsWrapperRef}
              isMapExploring={isMapExploring}
              onLocationSelection={handleLocationSelection}
              dateRange={dateRange}
              onDateRangeChange={onDateRangeChange}
            />



            <div
              ref={mainSearchContentRef}
              className={css.mainSearchContent}
              style={getMainSearchContentStyle}
            >

              <div
                ref={leftContentRef}
                className={classNames(css.leftContent, (isMapFullscreen || (isMobile && showMap)) && css.hidden)}
                style={
                  isMobileOverlayActive
                    ? {
                      '--mobile-panel-offset': `${isDraggingPanel ? panelOffset + PANEL_OFFSET_DRAG_HANDLE_OFFSET : panelOffset}px`,
                      zIndex: panelOpenProgress > 0.95 ? 101 : 99,
                      opacity: shouldForceFullOpacity
                        ? 1
                        : panelOpenProgress > 0 ? 0.55 + (panelOpenProgress * 2) : 0,

                    }
                    : undefined
                }


                {...panelPointerHandlers}
              >
                {(isMobile && showMap) && (
                  <div
                    className={css.mapcloseSwipeHandle}
                    {...createDragHandleHandlers(true)}
                  >
                    <span></span>
                  </div>
                )}

                {/* Desktop-only sticky search bar: date range | location | keyword + filters */}
                <DesktopSearchBar
                  queryParams={queryParams}
                  history={history}
                  routesConfiguration={routesConfiguration}
                  config={config}
                  intl={intl}
                  currentUserLocationInfo={currentUserLocationInfo}
                  selectedBounds={selectedBounds}
                  onActivateListing={onActivateListing}
                  algoliaListings={allListingsForProps}
                  algoliaStores={algoliaStores}
                  algoliaQueryInProgress={algoliaQueryInProgress}
                  currentUserCurrency={currentUserCurrency}
                  currentUser={currentUser}
                  exchangeRates={exchangeRates}
                  facets={facets}
                  facetsError={facetsError}
                  facetsInProgress={facetsInProgress}
                  showMap={showMap}
                  locale={locale}
                  defaultSort={defaultSort}
                  hasLocationAvailable={hasLocationAvailable}
                  isMapExploring={isMapExploring}
                  onLocationSelection={handleLocationSelection}
                  dateRange={dateRange}
                  onDateRangeChange={onDateRangeChange}
                />

                <div
                  style={
                    isMobileOverlayActive
                      ? {
                        opacity: shouldForceFullOpacity
                          ? 1
                          : panelOpenProgress > 0 ? 0.35 + (panelOpenProgress * 2) : 0,
                      }
                      : undefined
                  }
                >
                  <CustomHits
                    history={history}
                    currentUser={currentUser}
                    routesConfiguration={routesConfiguration}
                    currentUserCurrency={currentUserCurrency}
                    exchangeRates={exchangeRates}
                    selectedBounds={selectedBounds}
                    onActivateListing={onActivateListing}
                    onPrefetchListing={onPrefetchListing}
                    algoliaListings={algoliaListings}
                    algoliaQueryInProgress={algoliaQueryInProgress}
                    algoliaBackgroundQueryInProgress={algoliaBackgroundQueryInProgress}
                    algoliaQueryError={algoliaQueryError}
                    queryString={search.length ? search : ''}
                    currentUserCountryCode={currentUserLocationInfo?.country}
                    locale={locale}
                    selectedArea={selectedArea}
                    keyword={qKeyword}
                    activeFilters={activeFilters}
                    fallbackCity={currentUserLocationInfo?.city}
                    hasLocationAvailable={hasLocationAvailable}
                    isMapExploring={isMapExploring}
                    startDate={qStartDate}
                    endDate={qEndDate}
                  />
                </div>
              </div>
              <div
                className={classNames(css.rightContent, isMapFullscreen && css.fullscreenActive)}
              >
                <Map
                  className={css.map}
                  algoliaListings={relevantListingsForMap}
                  currentUserCurrency={currentUserCurrency}
                  intl={intl}
                  location={location}
                  exchangeRates={exchangeRates}
                  showMap={showMap}
                  selectedBounds={selectedBounds}
                  onClose={() => toggleMapHandler(false)}
                  activeListingId={props.activeListingId}
                  onActivateListing={onActivateListing}
                  isFullscreen={isMapFullscreen}
                  currentUserCountryCode={currentUserLocationInfo?.country}
                  panelOpenProgress={panelOpenProgress}
                  mapPreviewVisible={isMapPreviewVisible}
                  onMapMoveEnd={handleMapMoveEnd}
                  panelPointerHandlers={panelPointerHandlers}
                />
              </div>
            </div>
          </Column>
        </Grid>
      </Container>

    </main>
  );
}
